import { useSearchParams } from 'react-router-dom'

/** How a URL-state write treats browser history (TO-647, url-state REQ-003). */
export enum UrlHistoryMode {
	/** Rewrite the current entry — no new Back step (the default). */
	Replace = 'replace',
	/** Add one entry per write, so Back restores the previous value. */
	Push = 'push',
}

/**
 * Options for {@link useUrlState}.
 *
 * @property defaultValue - The value when the parameter is absent or rejected; never written to the URL.
 * @property parse - Turns the raw parameter into a value; return `undefined` to reject it. Omit for strings.
 * @property serialize - Turns a value into the parameter text. Defaults to `String(value)`.
 * @property history - Replace (default) or push a history entry on write.
 */
export interface UrlStateOptions<T> {
	defaultValue: T
	parse?: (raw: string) => T | undefined
	serialize?: (value: T) => string
	history?: UrlHistoryMode
}

/**
 * Options for {@link useTabParam}.
 *
 * @property key - The query parameter. Defaults to `tab`.
 * @property defaultTab - The tab when the parameter is absent or undeclared. Defaults to the first tab.
 * @property history - Replace (default) or push a history entry on write.
 */
export interface TabParamOptions<T extends string> {
	key?: string
	defaultTab?: T
	history?: UrlHistoryMode
}

// react-router computes each `setSearchParams` from the last *rendered* URL, so
// two writes in one event handler (filter change + page reset) lose the first.
// Writes in the same tick build on this pending query instead (url-state
// REQ-004); a microtask clears it once the handler has finished.
let pendingSearch: URLSearchParams | null = null

function startFromPending(rendered: URLSearchParams): URLSearchParams {
	if (pendingSearch === null) {
		queueMicrotask(() => {
			pendingSearch = null
		})
	}
	return new URLSearchParams(pendingSearch ?? rendered)
}

/**
 * One query parameter as typed, deep-linkable state (TO-647, UX standard §6).
 *
 * A screen keeps a tab, filter, page or search term here so that it can be shared,
 * survives reload, and comes back on browser Back. Only `key` is ever touched.
 *
 * @remarks
 * - **Read:** absent or rejected by `parse` → `defaultValue`. A read never navigates.
 * - **Write:** sets `key` to the serialized value. When that equals the serialized
 *   default, or is empty, the parameter is removed, so URLs stay clean.
 * - **Same tick:** several setters called in one handler all land in the URL.
 *
 * @example
 * ```tsx
 * const [status, setStatus] = useUrlState('status', { defaultValue: 'all' })
 * const [page, setPage] = useUrlState('page', { defaultValue: 1, parse: parsePage })
 * const onStatusChange = (next: string) => {
 *   setStatus(next)
 *   setPage(1)
 * }
 * ```
 */
export function useUrlState<T = string>(key: string, options: UrlStateOptions<T>): [T, (value: T) => void] {
	const { defaultValue, parse, serialize = String, history = UrlHistoryMode.Replace } = options
	const [params, setParams] = useSearchParams()

	const raw = params.get(key)
	const parsed = raw === null ? undefined : parse ? parse(raw) : (raw as T)
	const value = parsed === undefined ? defaultValue : parsed

	const setValue = (next: T) => {
		const text = serialize(next)
		const search = startFromPending(params)
		if (text === '' || text === serialize(defaultValue)) search.delete(key)
		else search.set(key, text)
		pendingSearch = search
		setParams(search, { replace: history === UrlHistoryMode.Replace })
	}

	return [value, setValue]
}

/**
 * The selected tab as a deep-linkable `?tab=` parameter, limited to `tabs`.
 *
 * @remarks
 * - An undeclared or absent parameter reads as `defaultTab` (the first tab unless given).
 * - The setter takes the `Tabs` primitive's selection key as-is (string or number,
 *   compared as a string), removes the parameter for the default tab, and ignores
 *   undeclared ids.
 *
 * @example
 * ```tsx
 * const [tab, setTab] = useTabParam(CUSTOMER_TABS)
 * <Tabs tabs={entries} selectedKey={tab} onSelectionChange={setTab} />
 * ```
 */
export function useTabParam<T extends string>(
	tabs: readonly T[],
	options: TabParamOptions<T> = {},
): [T, (key: string | number) => void] {
	const { key = 'tab', defaultTab = tabs[0], history } = options
	const isDeclaredTab = (id: string): id is T => (tabs as readonly string[]).includes(id)
	const [tab, setTab] = useUrlState<T>(key, {
		defaultValue: defaultTab,
		parse: (raw) => (isDeclaredTab(raw) ? raw : undefined),
		history,
	})

	const selectTab = (selected: string | number) => {
		const id = String(selected)
		if (isDeclaredTab(id)) setTab(id)
	}

	return [tab, selectTab]
}
